import type { BusbarSelectionResponse } from "../services/busbarSelection";
import type { BusbarSelectionWarningCode } from "../services/busbarSelectionContract";
import { CHECK_LABELS, unverifiedTitle } from "./busbarSelectionResultWords";
import { StatusIcon } from "./StatusIcon";
import { GROUP_TONE } from "./statusTone";

type WarningGroup =
  | "not-met"
  | "review-evidence"
  | "data-not-verified"
  | "fault-level"
  | "declared-evidence";

interface BusbarSelectionWarningPanelProps {
  result: BusbarSelectionResponse;
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive maps: a new code without a label or a group is a compile error. A busbar
// selection warning carries only a code and a message - there is no severity and no
// reference field on this contract, so none is shown.
const WARNING_CODE_LABELS: Record<BusbarSelectionWarningCode, string> = {
  PROSPECTIVE_CURRENT_NOT_LINKED: "Prospective current not linked to a fault run",
  FAULT_RUN_NOT_MAXIMUM_THREE_PHASE: "Fault run not a maximum three-phase study",
  FAULT_RUN_BUS_MISMATCH: "Fault run bus differs from the study bus",
  MARGIN_FACTOR_UNUSUALLY_LARGE: "Margin factor unusually large",
  EVIDENCE_REFERENCE_INCOMPLETE: "Data not verified",
  BUSBAR_SELECTION_MANUFACTURER_DECLARED: "Busbar evidence manufacturer-declared",
};

const WARNING_CODE_GROUPS: Record<BusbarSelectionWarningCode, WarningGroup> = {
  PROSPECTIVE_CURRENT_NOT_LINKED: "fault-level",
  FAULT_RUN_NOT_MAXIMUM_THREE_PHASE: "fault-level",
  FAULT_RUN_BUS_MISMATCH: "fault-level",
  // A margin factor above 2 may be a percentage typed as a multiplier: evidence to recheck.
  MARGIN_FACTOR_UNUSUALLY_LARGE: "review-evidence",
  // Evidence without a complete document reference: listed value by value in its own group.
  EVIDENCE_REFERENCE_INCOMPLETE: "data-not-verified",
  BUSBAR_SELECTION_MANUFACTURER_DECLARED: "declared-evidence",
};

// A check that failed comes first (there is nothing to accept), then the checks that need the
// engineer's review, then every value whose document reference is not complete (one line each,
// with the check that used it), then the fault level that is not the linked maximum three-phase
// study of the bus, and last the declared-evidence limit every result carries (GAP-022). A check that
// does not apply is not listed here: it is counted in the summary and shown in the detail with
// the engine's reason, and the frontend never infers a forgotten choice from it.
const GROUP_ORDER: readonly { group: WarningGroup; title: string }[] = [
  { group: "not-met", title: "Not met" },
  { group: "review-evidence", title: "Review required (evidence)" },
  { group: "data-not-verified", title: "Data not verified" },
  { group: "fault-level", title: "Fault level (review required)" },
  { group: "declared-evidence", title: "Declared evidence" },
];

type Item = { key: string; name: string; text: string; attribute: Record<string, string> };

// The checks and the codes of one group, in the engine's order.
function itemsOf(result: BusbarSelectionResponse, group: WarningGroup): Item[] {
  if (group === "data-not-verified") {
    const values = result.checks.flatMap((check) =>
      check.unverified.map((value) => ({
        key: value.path,
        name: `${CHECK_LABELS[check.key]}: ${value.label}`,
        text: unverifiedTitle(value),
        attribute: { "data-unverified-path": value.path },
      })),
    );
    const note = result.warnings.find((warning) => warning.code === "EVIDENCE_REFERENCE_INCOMPLETE");
    return note === undefined
      ? values
      : [
          ...values,
          {
            key: note.code,
            name: WARNING_CODE_LABELS[note.code],
            text: note.message,
            attribute: { "data-warning-code": note.code },
          },
        ];
  }
  const checks = result.checks
    .filter(
      (check) =>
        (group === "not-met" && check.status === "NO_SOLUTION") ||
        (group === "review-evidence" && check.status === "REVIEW_REQUIRED"),
    )
    .map((check) => ({
      key: `check-${check.key}`,
      name: CHECK_LABELS[check.key],
      text: check.reason,
      attribute: { "data-warning-check": check.key },
    }));
  const codes = result.warnings
    .map((warning, index) => ({ warning, index }))
    .filter(({ warning }) => WARNING_CODE_GROUPS[warning.code] === group)
    .map(({ warning, index }) => ({
      key: `${warning.code}-${index}`,
      name: WARNING_CODE_LABELS[warning.code],
      text: warning.message,
      attribute: { "data-warning-code": warning.code },
    }));
  return [...checks, ...codes];
}

export function BusbarSelectionWarningPanel({ result }: BusbarSelectionWarningPanelProps) {
  const groups = GROUP_ORDER.map(({ group, title }) => ({
    group,
    title,
    items: itemsOf(result, group),
  })).filter(({ items }) => items.length > 0);
  const count = groups.reduce((total, { items }) => total + items.length, 0);

  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {count === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <div data-warning-count={count}>
          {groups.map(({ group, title, items }) => (
            <section key={group} aria-label={title} data-warning-group={group}>
              <h4>
                <StatusIcon tone={GROUP_TONE[group] ?? "warning"} size={20} />
                {title}
              </h4>
              <ul>
                {items.map((item) => (
                  <li key={item.key} {...item.attribute}>
                    <strong>{item.name}</strong>
                    {": "}
                    {/* The engine's own sentence, kept as it was sent. */}
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </section>
  );
}

export { GROUP_ORDER, WARNING_CODE_GROUPS, WARNING_CODE_LABELS };

export default BusbarSelectionWarningPanel;
